import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components/macro";
import { useTranslation } from "react-i18next";
import TextInput from "commons/components/TextInput";
import Icon from "commons/components/Icon";
import Flexbox from "commons/components/Flexbox";
import { isEmailValid } from "commons/util/helpers";
import callApi from "commons/util/callApi";
import { useToast } from "commons/util/useToast";
import Loader from "commons/components/Loader";
import Honeypot from "commons/components/Honeypot";
import TextLink from "commons/components/TextLink";
import T from "prop-types";

const primaryButtonStyle = css`
  color: var(--neutral-200);
  font-weight: 600;
  background-color: var(--primary-100);
  background-image: radial-gradient(ellipse 75px 20px at 50% 50px, var(--primary-140) 0%, var(--primary-100) 100%);
  box-shadow: 0 0 0 3px rgba(251, 168, 190, 0.3), 0px 0px 16px rgba(251, 168, 190, 0.2);

  &:hover {
    box-shadow: 0 0 0 4px rgba(251, 168, 190, 0.4), 0px 0px 16px rgba(251, 168, 190, 0.7);
  }

  &:active {
    background-image: radial-gradient(ellipse 75px 20px at 50% 50px, var(--primary-140) 0%, var(--primary-90) 100%);
  }
`;

const glassyButtonStyle = css`
  color: var(--brown-100);
  font-weight: 300;
  background-color: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: var(--shadow-0), 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.5);
    box-shadow: var(--shadow-1), 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1);
  }
`;

const BigButton = styled.button`
  ${({ variant }) =>
    variant === "primary" &&
    css`
      ${primaryButtonStyle};
    `}

  ${({ variant }) =>
    variant === "glassy" &&
    css`
      ${glassyButtonStyle};
    `}

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: fit-content;
  padding: 10px 12px 10px 18px;
  border-radius: 40px;

  font-size: 16px;
  line-height: 20px;
  font-family: "Stack Sans Headline", sans-serif;
  white-space: nowrap;
  width: 240px;
`;

const SmallButton = styled.button`
  ${({ variant }) =>
    variant === "primary" &&
    css`
      ${primaryButtonStyle};
    `}

  ${({ variant }) =>
    variant === "glassy" &&
    css`
      ${glassyButtonStyle};
    `}

  border-radius: 40px;
  width: 40px;
  height: 40px;
  position: relative;
  flex-shrink: 0;
`;

const LoaderStyled = styled(Loader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
`;

const TextInputStyled = styled(TextInput)`
  margin-right: 8px;
  width: 240px;

  & > div {
    border-radius: 40px;
    &:after {
      border-radius: 40px;
    }
  }
`;

const SuccessMessage = styled.p`
  b {
    color: var(--primary-100);
    font-weight: 600;
  }
`;

const RequestAccessForm = ({ className, variant = "primary" }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [isActive, setIsActive] = useState(false);
  const [didRequestAccess, setDidRequestAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [waitlistCount, setWaitlistCount] = useState();
  const [honeypotValue, setHoneypotValue] = useState("");

  const inputRef = useRef();
  const inputWrapperRef = useRef();

  useEffect(() => {
    async function getCount() {
      try {
        const res = await callApi("waitlist/count", "get");
        setWaitlistCount(res.count);
      } catch (e) {
        console.log(e);
      }
    }

    getCount();
  }, []);

  useEffect(() => {
    if (emailError) setEmailError("");
  }, [email]);

  useEffect(() => {
    if (isActive) {
      inputRef.current?.focus();
    }
  }, [isActive]);

  async function handleSubmit() {
    if (honeypotValue) {
      // Honeypot check - if filled, silently reject
      setIsActive(false);
      setDidRequestAccess(true);
      return;
    }

    if (validateEmail()) {
      setIsLoading(true);
      try {
        await callApi("waitlist/email", "post", { email });
        setIsActive(false);
        setDidRequestAccess(true);
        setWaitlistCount(count => count + 1);
      } catch (e) {
        showToast(t("Common.GenericError"), "error");
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    }
  }

  function validateEmail() {
    if (!isEmailValid(email.trim())) {
      setEmailError(t("Authentication.InvalidEmail"));
      return false;
    }
    return true;
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (isActive && !email.trim() && !inputWrapperRef.current?.contains(e.target)) {
        setIsActive(false);
      }
    }

    if (isActive) {
      document.addEventListener("mouseup", handleClickOutside);
      return () => {
        document.removeEventListener("mouseup", handleClickOutside);
      };
    }
  }, [isActive, email, inputWrapperRef]);

  return (
    <div className={className}>
      {!isActive && !didRequestAccess && (
        <BigButton onClick={() => setIsActive(true)} variant={variant} data-umami-event="Request Access button">
          {t("Waitlist.RequestAccess")}
          <Icon name="arrow_forward" size={20} />
        </BigButton>
      )}

      {isActive && !didRequestAccess && (
        <Flexbox ref={inputWrapperRef}>
          <Honeypot value={honeypotValue} onChange={setHoneypotValue} />
          <TextInputStyled
            ref={inputRef}
            name="email"
            isActive={isActive}
            value={email}
            onChange={setEmail}
            autoFocus
            autoComplete="on"
            placeholder={t("Waitlist.EmailPlaceholder")}
            errorMessage={emailError}
            onKeyDown={e => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
          <SmallButton onClick={handleSubmit} variant={variant} data-umami-event="Request Access confirm button">
            {isLoading ? (
              <LoaderStyled containerHeight="20px" color="neutral-200" />
            ) : (
              <Icon name="arrow_forward" size={20} />
            )}
          </SmallButton>
        </Flexbox>
      )}

      {didRequestAccess && (
        <SuccessMessage>
          <b>Congrats!</b> You are #{waitlistCount} in line.
          <br />
          Want earlier access and a discount? Fill out{" "}
          <TextLink to={`https://tally.so/r/MelPOA?email=${email.trim()}`} isAbsolute data-umami-event="Survey link">
            this survey
          </TextLink>
        </SuccessMessage>
      )}
    </div>
  );
};

RequestAccessForm.propTypes = {
  className: T.string,
  variant: T.oneOf(["primary", "glassy"]),
};

export default RequestAccessForm;
